import { ActionButton, Dropdown, type IDropdownOption } from "@fluentui/react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { PreOpsAnswerChoice } from "../../api";
import { store } from "../../store";
import { type UUID } from "../../utils/uuid";
import SearchableDropdown from "../atoms/searchable-dropdown";
import { defaultPageSize } from "../../utils/constants";

export interface FilterPreOpsAnswerListProps {
    /** This callback function allows the filter to rerender
     * the parent component. */
    setPreOpsAnswerListFilter(filter: FilterPreOpsAnswerListQuery): void;
}

export type FilterPreOpsAnswerListQuery = {
    filteredChoices: PreOpsAnswerChoice[];
    filteredVehicles: UUID[];
    isFiltered: boolean;
};

@observer
export class FilterPreOpsAnswerList extends React.Component<FilterPreOpsAnswerListProps> {
    @observable private accessor filteredChoices: PreOpsAnswerChoice[] = [];
    @observable private accessor filteredVehicles: UUID[] = [];
    @observable private accessor isFiltered: boolean = false;

    constructor(props: FilterPreOpsAnswerListProps) {
        super(props);
        makeObservable(this);
    }

    private setPreOpsAnswerChoiceFilter(filter: PreOpsAnswerChoice): void {
        // Remove element if it is already clicked.
        if (this.filteredChoices.includes(filter)) {
            this.filteredChoices = this.filteredChoices.filter((id) => id !== filter);
            this.setPreOpsAnswerListFilter();
            return;
        }
        this.isFiltered = true;
        this.filteredChoices = [filter, ...this.filteredChoices];
        this.setPreOpsAnswerListFilter();
    }

    @action.bound private clearFilter(): void {
        this.isFiltered = false;
        this.filteredChoices = [];
        this.filteredVehicles = [];
        this.setPreOpsAnswerListFilter();
    }

    private setPreOpsAnswerListFilter(): void {
        const filterQuery: FilterPreOpsAnswerListQuery = {
            filteredChoices: this.filteredChoices,
            filteredVehicles: this.filteredVehicles,
            isFiltered: this.isFiltered,
        };
        this.props.setPreOpsAnswerListFilter(filterQuery);
    }

    @action.bound private setVehicleFilter(vehicleId?: UUID, selected?: boolean): void {
        if (!vehicleId) {
            return;
        }
        if (selected) {
            this.isFiltered = true;
            this.filteredVehicles = [vehicleId, ...this.filteredVehicles];
            this.setPreOpsAnswerListFilter();
        } else {
            this.filteredVehicles = this.filteredVehicles.filter((id) => id !== vehicleId);
            this.setPreOpsAnswerListFilter();
        }
    }

    public render(): JSX.Element {
        return (
            <>
                <Dropdown
                    styles={{ dropdown: { width: 200 } }}
                    options={Object.values(PreOpsAnswerChoice).map((choice) => {
                        return {
                            key: choice,
                            text: store.i18n.t(`schema.preOpsAnswerChoice.${choice}`),
                        };
                    })}
                    placeholder={store.i18n.t("listPreOpsAnswers.column.choice.name")}
                    selectedKeys={this.filteredChoices}
                    onChange={(_e, item?: IDropdownOption) => {
                        if (!item) {
                            return;
                        }
                        this.setPreOpsAnswerChoiceFilter(item.key as PreOpsAnswerChoice);
                    }}
                    multiSelect
                />
                <SearchableDropdown
                    placeholder={store.i18n.t("listPreOpsAnswers.column.vehicle.name")}
                    formatEntity={store.i18n.formatVehicle}
                    repository={store.repositoryVehicles}
                    query={{ pageSize: defaultPageSize }}
                    onChange={this.setVehicleFilter}
                    selectedKeys={this.filteredVehicles}
                    multiSelect
                />

                <ActionButton
                    iconProps={{ iconName: "clearFilter" }}
                    allowDisabledFocus
                    disabled={!this.isFiltered}
                    checked={true}
                    onClick={this.clearFilter}
                ></ActionButton>
            </>
        );
    }
}
